/**
 * Quota Monitoring API
 * Track NewsData.io, Finnhub, and Article Pool health
 */

import { apiRequest } from './client';

export interface QuotaService {
  service: string;
  limit: number;
  used: number;
  remaining: number;
  percentUsed: number;
  status: 'healthy' | 'warning' | 'critical';
  resetTime: string;
  allocations?: {
    scheduled?: number;
    emergency?: number;
    onDemand?: number;
  };
  metrics?: {
    emergencyUsed?: number;
    lastEmergencyCall?: string;
  };
}

export interface ArticlePoolHealth {
  status: 'healthy' | 'warning' | 'critical';
  message: string;
  metrics: {
    totalArticles: number;
    symbolsCovered: number;
    oldestArticleAge: number; // hours
    averageArticleAge: number; // hours
    lastHarvestTime?: string;
    lastHarvestStatus?: string;
    hitRate?: number; // percentage
    articlesLast24h: number;
    articlesOlderThan24h: number;
    breakdown: {
      byCategory: Record<string, number>;
      bySymbol: Record<string, number>;
    };
    storage?: {
      kv: { articles: number; pools: number };
      do: { articles: number; pools: number };
      syncStatus: 'healthy' | 'degraded' | 'broken';
    };
  };
}

export interface QuotaReport {
  quotas: QuotaService[];
  articlePool?: ArticlePoolHealth;
  summary: {
    totalServices: number;
    servicesAtRisk: number;
    overallStatus: 'healthy' | 'warning' | 'critical';
  };
}

export interface QuotaHistory {
  yesterday: {
    service: string;
    used: number;
    limit: number;
    date: string;
  } | null;
  note: string;
}

export interface WarmingRunResult {
  symbol: string;
  data_refreshed: number;      // 0 or 1
  timestamp_touched: number;    // 0 or 1
  duration_ms: number;
  error: string | null;
  triggered_by: string;
}

export interface WarmingRunGroup {
  timestamp: string;
  triggeredBy: string;
  results: WarmingRunResult[];
  summary: {
    total: number;
    dataRefreshed: number;
    timestampOnly: number;
    failed: number;
    totalDuration: number;
  };
}

export interface WarmingStats {
  symbol: string;
  total_runs: number;
  data_refreshed_count: number;
  timestamp_touched_count: number;
  failed_count: number;
  data_refresh_rate: number;
  avg_duration_ms: number;
  last_run: string;
}

export interface WarmingTimelinePoint {
  date: string;
  total_operations: number;
  successful_refreshes: number;
  success_rate: number;
  avg_duration_ms: number;
}

/**
 * Get quota status for all services
 */
export async function getQuotaReport(): Promise<QuotaReport> {
  return apiRequest<QuotaReport>('/api/quota');
}

/**
 * Get NewsData.io quota only
 */
export async function getNewsDataQuota(): Promise<QuotaService> {
  return apiRequest<QuotaService>('/api/quota/newsdata');
}

/**
 * Get historical quota usage
 */
export async function getQuotaHistory(): Promise<QuotaHistory> {
  return apiRequest<QuotaHistory>('/api/quota/history');
}

/**
 * Get warming history from D1 (real worker results)
 */
export async function getWarmingHistory(limit: number = 10): Promise<WarmingRunGroup[]> {
  return apiRequest<WarmingRunGroup[]>(`/api/quota/warming-history?limit=${limit}`);
}

/**
 * Get warming statistics by symbol
 */
export async function getWarmingStats(days: number = 7): Promise<WarmingStats[]> {
  return apiRequest<WarmingStats[]>(`/api/quota/warming-stats?days=${days}`);
}

/**
 * Get warming timeline (success rate over time)
 */
export async function getWarmingTimeline(days: number = 30): Promise<WarmingTimelinePoint[]> {
  return apiRequest<WarmingTimelinePoint[]>(`/api/quota/warming-timeline?days=${days}`);
}

/**
 * Get enhanced article pool status with DO/KV metrics
 */
export async function getArticlePoolEnhancedStatus(): Promise<any> {
  return apiRequest<any>('/api/admin/article-pool/enhanced-status');
}
